import {AnyAction, createAction, createAsyncThunk, Dispatch, ThunkDispatch} from '@reduxjs/toolkit'

import {eq, isNumber} from '@shared/helpers/lang'

import {PaymentTabMethod, PaymentTabMethodType, PayMethod, SubscribePayMethodType} from '@web/core/constants'
import getApi, {createApiAsyncThunk, createWrapper} from '@web/helpers/api'
import {adapterChargePointSelector, ChargePointState} from '@web/slices/chargePoint/slice'
import {ExpenseDetailsState} from '@web/slices/expenseDetails/slice'
import {ModalState} from '@web/slices/modal/slice'
import {PointPaymentState} from '@web/slices/pointPayment/slice'
import {
    easyPayCardsSelector,
    PriorityPaymentMethodState,
    selectEasyCardId,
    selectSecondEasyCardId,
} from '@web/slices/priorityPaymentMethod/slice'
import {RewardPointState} from '@web/slices/rewardPoint/slice'

import {EasyAccountsResult} from './interfaces'

type RootState = {
    chargePoint: ChargePointState
    expenseDetails: ExpenseDetailsState
    modal: ModalState
    pointPayment: PointPaymentState
    priorityPaymentMethod: PriorityPaymentMethodState
    rewardPoint: RewardPointState
}

type ThunkApi<RejectValue = unknown> = {
    state: RootState
    dispatch: ThunkDispatch<RootState, unknown, AnyAction> & Dispatch<AnyAction>
    rejectValue: RejectValue
}

type AdapterState = {
    rechargePoint?: {
        /** 포인트 충전 사용 여부 */
        rechargePointEnabled?: boolean
        /** 충전할 포인트 금액 */
        rechargingAmount?: number
    }
    selectedMethod: SubscribePayMethodType
    /** 포인트 전액사용 on으로 변경 필요 여부 */
    shouldUsePointAll?: boolean
}

const api = getApi(`/orderApi/paymentMethods`)

/** 결제수단 선택 Action */
export const selectPayMethod = createAction(
    'priorityPaymentMethod/selectPayMethod',
    (payMethod: SubscribePayMethodType, adapterState: AdapterState) => {
        const rechargePointEnabled = payMethod === PayMethod.RECHARGE_POINT
        const selectedPayMethod = payMethod === PayMethod.RECHARGE_POINT ? PayMethod.NONE : payMethod

        return {
            payload: {
                rechargePoint: {
                    rechargingAmount: adapterState.rechargePoint?.rechargingAmount ?? 0,
                    enable: rechargePointEnabled,
                },
                selectedPayMethod,
                shouldUsePointAll: !!adapterState.shouldUsePointAll,
            },
        }
    },
)

/** 2순위 결제수단 선택 Action */
export const selectSecondPayMethod = createAction(
    'priorityPaymentMethod/selectSecondPayMethod',
    (payMethod: SubscribePayMethodType) => {
        return {payload: payMethod}
    },
)

/**
 * 결제수단 tab 선택
 * - 포인트 충전결제 Tab 선택
 * - 간편 계좌 Tab 선택
 * - 간편 카드 Tab 선택
 * - 일반결제 Tab 선택
 */
export const selectPayMethodTab = createAsyncThunk<
    void,
    {
        payMethod: PaymentTabMethodType
        shouldUsePointAll?: boolean
    },
    ThunkApi
>('priorityPaymentMethod/selectPayMethodTab', async ({payMethod, shouldUsePointAll}, {dispatch, getState}) => {
    const eqPayMethod = eq(payMethod)
    let nextPayMethod = null
    if (eqPayMethod(PaymentTabMethod.RECHARGE)) {
        // 포인트 충전결제
        nextPayMethod = PayMethod.RECHARGE_POINT
    } else if (eqPayMethod(PaymentTabMethod.EASY_CARD)) {
        // 간편카드결제
        nextPayMethod = PayMethod.CARD_EASY_PAY
    } else if (eqPayMethod(PaymentTabMethod.EASY_ACCOUNT)) {
        // 간편계좌결제
        nextPayMethod = PayMethod.BANK_EASY_PAY
    } else if (eqPayMethod(PaymentTabMethod.MOBILE)) {
        nextPayMethod = PayMethod.MOBILE
    }
    if (nextPayMethod) {
        dispatch(
            selectPayMethod(nextPayMethod, {
                ...adapterChargePointSelector(getState()),
                shouldUsePointAll,
            }),
        )
    }

    return undefined
})

/** 카드 간편결제에서 하나의 카드 | 간편결제 카드 추가 선택 */
export const selectCardEasyPay = createAsyncThunk<void, string | number, ThunkApi>(
    'priorityPaymentMethod/selectCardEasyPay',
    async (cardIdOrIdx, {dispatch, getState}) => {
        // string인 경우 id, number인 경우 idx
        const cardID = isNumber(cardIdOrIdx)
            ? (easyPayCardsSelector(getState())[cardIdOrIdx]?.cardId ?? '')
            : cardIdOrIdx
        dispatch(selectEasyCardId(cardID))
    },
)

/**
 * 2차 결제수단 tab 선택
 * - 선택안함
 * - 간편 계좌 Tab 선택
 * - 간편 카드 Tab 선택
 */
export const selectSecondPayMethodTab = createAsyncThunk<void, PaymentTabMethodType, ThunkApi>(
    'priorityPaymentMethod/selectSecondPayMethodTab',
    async (payMethod, {dispatch}) => {
        const eqPayMethod = eq(payMethod)
        let nextPayMethod = null
        if (eqPayMethod(PaymentTabMethod.EASY_CARD)) {
            // 간편카드결제
            nextPayMethod = PayMethod.CARD_EASY_PAY
        } else if (eqPayMethod(PaymentTabMethod.EASY_ACCOUNT)) {
            // 간편계좌결제
            nextPayMethod = PayMethod.BANK_EASY_PAY
        } else if (eqPayMethod(PaymentTabMethod.NONE)) {
            nextPayMethod = PayMethod.NONE
        }

        if (nextPayMethod) {
            dispatch(selectSecondPayMethod(nextPayMethod))
        }
    },
)

/**
 * 카드 간편결제에서 하나의 카드 | 간편결제 카드 추가 선택
 */
export const selectSecondCardEasyPay = createAsyncThunk<void, string | number, ThunkApi>(
    'priorityPaymentMethod/selectSecondCardEasyPay',
    async (cardIdOrIdx, {dispatch, getState}) => {
        // string인 경우 id, number인 경우 idx
        const cardID = isNumber(cardIdOrIdx)
            ? (easyPayCardsSelector(getState())[cardIdOrIdx]?.cardId ?? '')
            : cardIdOrIdx
        dispatch(selectSecondEasyCardId(cardID))
    },
)

/** 간편결제 계좌 정보 조회 */
export const getEasyAccounts = createApiAsyncThunk<EasyAccountsResult, void, ThunkApi>(
    'priorityPaymentMethod/fetchEasyAccounts',
    async () => {
        return createWrapper(api.get<{result: EasyAccountsResult}>('/common/easyAccounts'))
    },
)
